import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { INVOICE_STATUSES, INVOICE_STATUS_COLORS } from '@/lib/constants';
import { generateMonthlyInvoices, formatCurrency, calculateWeeks } from '@/lib/dateUtils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import { ArrowLeft, Edit, Save, DollarSign, FileText, Calendar as CalendarIcon, AlertTriangle, Download } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import * as XLSX from 'xlsx';

export default function InvoiceView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [regularInvoices, setRegularInvoices] = useState([]);
  const [adhocForProject, setAdhocForProject] = useState([]);
  const [editingDateIdx, setEditingDateIdx] = useState(null);

  const loadData = async () => {
    try {
      const [p, allAdhocs] = await Promise.all([api.getProject(id), api.getAdhocInvoices()]);
      if (!p) { navigate('/'); return; }
      setProject(p);

      const projAdhocs = allAdhocs.filter(a => a.projectId === p.id || (!a.projectId && a.projectName === p.projectName));
      setAdhocForProject(projAdhocs);

      const numWeeks = calculateWeeks(p.startDate, p.endDate);
      const generated = generateMonthlyInvoices(p.startDate, p.endDate, p.resources || [], numWeeks);

      if (p.invoices && p.invoices.length > 0) {
        const merged = generated.map(gen => {
          const saved = p.invoices.find(inv => inv.monthKey === gen.monthKey);
          if (saved) {
            return {
              ...gen,
              invoiceValue: saved.invoiceValue !== undefined ? saved.invoiceValue : gen.invoiceValue,
              consumptionAmount: saved.consumptionAmount !== undefined ? saved.consumptionAmount : gen.consumptionAmount,
              invoiceNumber: saved.invoiceNumber || gen.invoiceNumber || '',
              invoiceDate: saved.invoiceDate || gen.invoiceDate,
              invoiceDateFormatted: saved.invoiceDateFormatted || gen.invoiceDateFormatted,
              status: saved.status || gen.status,
            };
          }
          return gen;
        });
        setRegularInvoices(merged);
      } else {
        setRegularInvoices(generated);
      }
    } catch {
      navigate('/');
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadData(); }, [id]);

  // Combined list: regular + ad-hoc, sorted by monthKey, with cumulative remaining
  const combinedInvoices = useMemo(() => {
    const totalEstimate = regularInvoices.length > 0 ? regularInvoices[0].totalEstimate : 0;
    const regularItems = regularInvoices.map((inv, idx) => ({
      ...inv,
      _type: 'regular',
      _regularIdx: idx,
    }));
    const adhocItems = adhocForProject.map(a => ({
      monthKey: a.monthKey,
      month: a.month || a.monthKey,
      invoiceValue: a.invoiceValue || 0,
      consumptionAmount: a.consumptionAmount ?? a.invoiceValue ?? 0,
      invoiceNumber: a.invoiceNumber || '',
      invoiceDate: a.invoiceDate || '',
      invoiceDateFormatted: a.invoiceDateFormatted || '',
      status: a.status || 'po_awaited',
      totalEstimate,
      _type: 'adhoc',
      _adhocId: a.id,
    }));
    const all = [...regularItems, ...adhocItems].sort((a, b) => {
      if (a.monthKey !== b.monthKey) return a.monthKey.localeCompare(b.monthKey);
      // Regular before ad-hoc within same month
      return a._type === 'regular' ? -1 : 1;
    });
    // Recalculate cumulative remaining across the full combined list
    let cumInv = 0, cumCon = 0;
    return all.map(inv => {
      cumInv += inv.invoiceValue;
      cumCon += (inv.consumptionAmount ?? inv.invoiceValue);
      return {
        ...inv,
        remainingValue: Math.round(totalEstimate - cumInv),
        remainingConsumption: Math.round(totalEstimate - cumCon),
      };
    });
  }, [regularInvoices, adhocForProject]);

  const updateRegularField = (regularIdx, field, value) => {
    setRegularInvoices(prev => {
      const updated = [...prev];
      updated[regularIdx] = { ...updated[regularIdx], [field]: value };
      return updated;
    });
  };

  const updateInvoiceValue = (regularIdx, value) => {
    setRegularInvoices(prev => {
      const updated = [...prev];
      const numVal = value === '' ? 0 : Number(value);
      updated[regularIdx] = { ...updated[regularIdx], invoiceValue: isNaN(numVal) ? 0 : numVal };
      return updated;
    });
  };

  const updateConsumptionAmount = (regularIdx, value) => {
    setRegularInvoices(prev => {
      const updated = [...prev];
      updated[regularIdx] = { ...updated[regularIdx], consumptionAmount: Number(value) || 0 };
      return updated;
    });
  };

  const updateInvoiceDate = (regularIdx, date) => {
    if (!date) return;
    const formatted = format(date, 'yyyy-MM-dd');
    const display = format(date, 'MMM d, yyyy');
    setRegularInvoices(prev => {
      const updated = [...prev];
      updated[regularIdx] = { ...updated[regularIdx], invoiceDate: formatted, invoiceDateFormatted: display };
      return updated;
    });
    setEditingDateIdx(null);
  };

  const handleSave = async () => {
    if (project.status === 'draft' || project.status === 'proposal') {
      toast.error('Cannot save invoices for projects in Draft or Proposal phase. Move to SoW or Execute first.');
      return;
    }
    try {
      const updatedProject = { ...project, invoices: regularInvoices };
      await api.updateProject(project.id, updatedProject);
      toast.success('Invoices saved successfully');
    } catch {
      toast.error('Failed to save invoices');
    }
  };

  const handleExportExcel = () => {
    if (!project || combinedInvoices.length === 0) return;
    const data = combinedInvoices.map(inv => ({
      'Month': inv.month,
      'Type': inv._type === 'adhoc' ? 'Ad-hoc' : 'Regular',
      'Project Name': project.projectName,
      'Invoice #': inv.invoiceNumber || '',
      'Invoice Value ($)': inv.invoiceValue,
      'Consumption Amt ($)': inv.consumptionAmount ?? inv.invoiceValue,
      'Total Estimate ($)': inv.totalEstimate,
      'Remaining Invoice ($)': inv.remainingValue,
      'Remaining Consumption ($)': inv.remainingConsumption,
      'Invoice Date': inv.invoiceDateFormatted || '',
      'Status': INVOICE_STATUSES.find(s => s.value === inv.status)?.label || inv.status,
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Invoices');
    XLSX.writeFile(wb, `${project.id}_invoices.xlsx`);
    toast.success('Invoice table exported to Excel');
  };

  if (!project) return null;

  const isInvoiceBlocked = project.status === 'draft' || project.status === 'proposal';

  const totalEstimate = regularInvoices.length > 0 ? regularInvoices[0].totalEstimate : 0;
  const totalInvoiced = combinedInvoices.length > 0 ? totalEstimate - combinedInvoices[combinedInvoices.length - 1].remainingValue : 0;
  const totalConsumption = combinedInvoices.length > 0 ? totalEstimate - combinedInvoices[combinedInvoices.length - 1].remainingConsumption : 0;
  const remainingInvoice = combinedInvoices.length > 0 ? combinedInvoices[combinedInvoices.length - 1].remainingValue : totalEstimate;
  const remainingConsumption = combinedInvoices.length > 0 ? combinedInvoices[combinedInvoices.length - 1].remainingConsumption : totalEstimate;
  const adhocInvoiced = adhocForProject.reduce((sum, a) => sum + (a.invoiceValue || 0), 0);
  const adhocConsumption = adhocForProject.reduce((sum, a) => sum + (a.consumptionAmount ?? a.invoiceValue ?? 0), 0);

  return (
    <div data-testid="invoice-view-page">
      <Toaster theme="dark" />

      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/')} data-testid="back-to-dashboard-btn" className="text-zinc-400 hover:text-white hover:bg-zinc-900">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="font-manrope font-bold text-2xl sm:text-3xl tracking-tight text-white" data-testid="invoice-title">Invoices</h1>
            <p className="text-zinc-500 text-sm mt-0.5 font-manrope flex items-center gap-2">
              <span className="font-mono text-brand-500">{project.id}</span>
              <span className="text-zinc-700">|</span>
              {project.projectName}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/estimate/${project.id}`)} data-testid="edit-estimate-btn" className="text-zinc-400 hover:text-white hover:bg-zinc-900 font-medium">
            <Edit className="w-4 h-4 mr-2" />
            Edit Estimate
          </Button>
          <Button data-testid="save-invoices-btn" onClick={handleSave} className="bg-brand-500 text-white hover:bg-brand-400 hover:shadow-[0_0_20px_-5px_rgba(168,0,151,0.5)] font-semibold active:scale-95 transition-all duration-200">
            <Save className="w-4 h-4 mr-2" />
            Save Invoices
          </Button>
        </div>
      </div>

      {isInvoiceBlocked && (
        <div className="mb-6 p-4 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-start gap-3" data-testid="invoice-blocked-banner">
          <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
          <div>
            <p className="font-manrope font-semibold text-sm text-amber-300">Invoicing Not Available</p>
            <p className="text-xs text-amber-400/70 mt-0.5">
              This project is in <span className="font-semibold">{project.status === 'draft' ? 'Draft' : 'Proposal'}</span> phase. Invoices can only be created when the project moves to SoW or Execute status.
            </p>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
        <Card className="bg-zinc-950 border-zinc-900">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-brand-500/10 border border-brand-500/20 flex items-center justify-center"><DollarSign className="w-5 h-5 text-brand-400" /></div>
            <div>
              <p className="text-xs uppercase tracking-widest text-zinc-500 font-manrope">Total Estimate</p>
              <p className="font-mono text-lg font-bold text-white" data-testid="total-estimate-value">{formatCurrency(totalEstimate)}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-950 border-zinc-900">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center"><FileText className="w-5 h-5 text-emerald-400" /></div>
            <div>
              <p className="text-xs uppercase tracking-widest text-zinc-500 font-manrope">Total Invoiced</p>
              <p className="font-mono text-lg font-bold text-white" data-testid="total-invoiced-value">{formatCurrency(totalInvoiced)}</p>
              {adhocInvoiced > 0 && <p className="text-xs text-zinc-500 font-mono">incl. {formatCurrency(adhocInvoiced)} ad-hoc</p>}
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-950 border-zinc-900">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-violet-500/10 border border-violet-500/20 flex items-center justify-center"><DollarSign className="w-5 h-5 text-violet-400" /></div>
            <div>
              <p className="text-xs uppercase tracking-widest text-zinc-500 font-manrope">Total Consumption</p>
              <p className="font-mono text-lg font-bold text-white" data-testid="total-consumption-value">{formatCurrency(totalConsumption)}</p>
              {adhocConsumption > 0 && <p className="text-xs text-zinc-500 font-mono">incl. {formatCurrency(adhocConsumption)} ad-hoc</p>}
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-950 border-zinc-900">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center justify-center"><FileText className="w-5 h-5 text-amber-400" /></div>
            <div>
              <p className="text-xs uppercase tracking-widest text-zinc-500 font-manrope">Remaining Invoice</p>
              <p className={`font-mono text-lg font-bold ${remainingInvoice < 0 ? 'text-red-400' : 'text-white'}`} data-testid="remaining-invoice-value">{formatCurrency(remainingInvoice)}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-950 border-zinc-900">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-rose-500/10 border border-rose-500/20 flex items-center justify-center"><DollarSign className="w-5 h-5 text-rose-400" /></div>
            <div>
              <p className="text-xs uppercase tracking-widest text-zinc-500 font-manrope">Remaining Consumption</p>
              <p className={`font-mono text-lg font-bold ${remainingConsumption < 0 ? 'text-red-400' : 'text-white'}`} data-testid="remaining-consumption-value">{formatCurrency(remainingConsumption)}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Negative balance warning */}
      {(remainingInvoice < 0 || remainingConsumption < 0) && (
        <div className="mb-6 p-4 rounded-lg bg-red-500/10 border border-red-500/20 flex items-start gap-3" data-testid="negative-balance-warning">
          <AlertTriangle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
          <div>
            <p className="font-manrope font-semibold text-sm text-red-300">Negative Balance Detected</p>
            <p className="text-xs text-red-400/70 mt-0.5">
              {remainingInvoice < 0 && <span>Invoice balance is <span className="font-semibold">{formatCurrency(remainingInvoice)}</span>. </span>}
              {remainingConsumption < 0 && <span>Consumption balance is <span className="font-semibold">{formatCurrency(remainingConsumption)}</span>. </span>}
              Please adjust invoices so balances become zero.
            </p>
          </div>
        </div>
      )}

      {/* Invoice Table */}
      {combinedInvoices.length > 0 ? (
        <Card className="bg-zinc-950 border-zinc-900" data-testid="invoices-table-card">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="font-manrope font-bold text-base text-white">Monthly Invoice Schedule</CardTitle>
              {adhocForProject.length > 0 && (
                <Badge className="bg-amber-500/10 text-amber-400 border border-amber-500/20 font-mono text-xs rounded-full">
                  {adhocForProject.length} ad-hoc entr{adhocForProject.length === 1 ? 'y' : 'ies'}
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-800 hover:bg-transparent">
                    <TableHead className="text-xs uppercase tracking-widest text-zinc-500 font-manrope min-w-[140px]">Month</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest text-zinc-500 font-manrope min-w-[160px]">Project Name</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest text-zinc-500 font-manrope min-w-[130px]">Invoice #</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest text-zinc-500 font-manrope min-w-[150px]">Invoice Value</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest text-zinc-500 font-manrope min-w-[150px]">Consumption Amt</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest text-zinc-500 font-manrope min-w-[130px] text-right">Total Estimate</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest text-zinc-500 font-manrope min-w-[130px] text-right">Rem. Invoice</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest text-zinc-500 font-manrope min-w-[130px] text-right">Rem. Consumption</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest text-zinc-500 font-manrope min-w-[160px]">Invoice Date</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest text-zinc-500 font-manrope min-w-[160px]">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {combinedInvoices.map((inv, idx) => {
                    const isAdhoc = inv._type === 'adhoc';
                    const statusColor = INVOICE_STATUS_COLORS[inv.status] || INVOICE_STATUS_COLORS.po_awaited;
                    return (
                      <TableRow key={isAdhoc ? `adhoc-${inv._adhocId}` : `reg-${inv._regularIdx}`} className={`border-zinc-900 hover:bg-zinc-900/30 ${isAdhoc ? 'bg-amber-500/[0.03]' : ''}`} data-testid={`invoice-row-${idx}`}>
                        <TableCell className="font-manrope font-medium text-sm text-white" data-testid={`invoice-month-${idx}`}>
                          <span className="flex items-center gap-2">
                            {inv.month}
                            {isAdhoc && <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20 text-[10px] px-1.5 py-0 rounded-full font-mono">Ad-hoc</Badge>}
                          </span>
                        </TableCell>
                        <TableCell className="font-manrope text-sm text-zinc-300">{project.projectName}</TableCell>
                        <TableCell className="p-2">
                          {isAdhoc ? (
                            <span className="font-mono text-xs text-zinc-400 pl-2">{inv.invoiceNumber || '-'}</span>
                          ) : (
                            <Input data-testid={`invoice-number-input-${idx}`} value={inv.invoiceNumber || ''} onChange={e => updateRegularField(inv._regularIdx, 'invoiceNumber', e.target.value)} placeholder="INV-..." className="bg-zinc-950 border-zinc-800 font-mono text-xs h-8 w-28 focus:border-brand-500 focus:ring-1 focus:ring-brand-500/20 placeholder:text-zinc-700" />
                          )}
                        </TableCell>
                        <TableCell className="p-2">
                          {isAdhoc ? (
                            <span className="font-mono text-sm text-zinc-400 pl-2">{formatCurrency(inv.invoiceValue)}</span>
                          ) : (
                            <div className="relative">
                              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500 text-xs font-mono">$</span>
                              <Input type="number" data-testid={`invoice-value-input-${idx}`} value={inv.invoiceValue} onChange={e => updateInvoiceValue(inv._regularIdx, e.target.value)} className="bg-zinc-950 border-zinc-800 font-mono text-sm h-8 pl-5 w-32 focus:border-brand-500 focus:ring-1 focus:ring-brand-500/20" />
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="p-2">
                          {isAdhoc ? (
                            <span className="font-mono text-sm text-zinc-400 pl-2">{formatCurrency(inv.consumptionAmount ?? inv.invoiceValue)}</span>
                          ) : (
                            <div className="relative">
                              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500 text-xs font-mono">$</span>
                              <Input type="number" data-testid={`consumption-amount-input-${idx}`} value={inv.consumptionAmount ?? inv.invoiceValue} onChange={e => updateConsumptionAmount(inv._regularIdx, e.target.value)} className="bg-zinc-950 border-zinc-800 font-mono text-sm h-8 pl-5 w-32 focus:border-brand-500 focus:ring-1 focus:ring-brand-500/20" />
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm text-zinc-300">{formatCurrency(inv.totalEstimate)}</TableCell>
                        <TableCell className="text-right">
                          <span className={`font-mono text-sm ${inv.remainingValue < 0 ? 'text-red-400' : 'text-zinc-300'}`}>{formatCurrency(inv.remainingValue)}</span>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className={`font-mono text-sm ${inv.remainingConsumption < 0 ? 'text-red-400' : 'text-zinc-300'}`}>{formatCurrency(inv.remainingConsumption)}</span>
                        </TableCell>
                        <TableCell className="p-2">
                          {isAdhoc ? (
                            <span className="font-mono text-xs text-zinc-400 pl-2">{inv.invoiceDateFormatted || '-'}</span>
                          ) : (
                            <Popover open={editingDateIdx === idx} onOpenChange={(open) => setEditingDateIdx(open ? idx : null)}>
                              <PopoverTrigger asChild>
                                <Button variant="outline" data-testid={`invoice-date-btn-${idx}`} className="w-full justify-start text-left font-mono text-xs bg-zinc-950 border-zinc-800 hover:bg-zinc-900 hover:border-zinc-700 h-8 text-zinc-300">
                                  <CalendarIcon className="w-3.5 h-3.5 mr-1.5 text-zinc-500" />
                                  {inv.invoiceDateFormatted || 'Set date'}
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto p-0 bg-zinc-950 border-zinc-800" align="start">
                                <Calendar mode="single" selected={inv.invoiceDate ? parseISO(inv.invoiceDate) : undefined} onSelect={(date) => updateInvoiceDate(inv._regularIdx, date)} className="text-white" />
                              </PopoverContent>
                            </Popover>
                          )}
                        </TableCell>
                        <TableCell className="p-2">
                          {isAdhoc ? (
                            <Badge className={`${statusColor} text-xs px-2 py-0.5 rounded-full`}>
                              {INVOICE_STATUSES.find(s => s.value === inv.status)?.label || inv.status}
                            </Badge>
                          ) : (
                            <Select value={inv.status} onValueChange={(val) => updateRegularField(inv._regularIdx, 'status', val)}>
                              <SelectTrigger data-testid={`invoice-status-select-${idx}`} className={`bg-transparent border h-8 text-xs w-40 ${statusColor}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-zinc-950 border-zinc-800">
                                {INVOICE_STATUSES.map(s => (
                                  <SelectItem key={s.value} value={s.value} className="text-zinc-200 text-xs focus:bg-zinc-900 focus:text-white">{s.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            <div className="flex justify-end p-4 border-t border-zinc-900">
              <Button variant="ghost" size="sm" onClick={handleExportExcel} data-testid="export-invoices-btn" className="text-zinc-400 hover:text-white hover:bg-zinc-900 font-medium">
                <Download className="w-4 h-4 mr-2" />
                Export Excel
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-zinc-950 border-zinc-900">
          <CardContent className="p-8 text-center">
            <CalendarIcon className="w-8 h-8 mx-auto mb-3 text-zinc-700" />
            <p className="text-zinc-500 text-sm">No invoices to display. Ensure the estimate has dates and resource allocations configured.</p>
            <Button variant="ghost" className="mt-4 text-brand-400 hover:text-brand-300" onClick={() => navigate(`/estimate/${project.id}`)} data-testid="go-to-estimate-btn">
              <Edit className="w-4 h-4 mr-2" />
              Edit Estimate
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
